import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import { BringzzOverlay, BringzzText } from '@bringzz/components';
import { useNavigation } from 'context/NavigationContext';
import { Scanner } from '@yudiel/react-qr-scanner';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from 'components/ErrorBoundary';

// CameraStream Component (handles camera video stream using @yudiel/react-qr-scanner)
const CameraStream = forwardRef(({ setDetectedText, onCameraDetectionChange, onFacingModeChange }, ref) => {
    const { backPress } = useNavigation();
    const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
    const [permissionDeniedPermanently, setPermissionDeniedPermanently] = useState(false);
    const [facingMode, setFacingMode] = useState('environment'); // 'environment' = back camera, 'user' = front camera
    const [isTorchOn, setIsTorchOn] = useState(false);
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [scannerKey, setScannerKey] = useState(0); // Key to force Scanner remount
    const videoRef = useRef(null);
    const scannerRef = useRef(null);

    // Detect available cameras on mount and after permission granted
    useEffect(() => {
        const detectCameras = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                console.log('[CameraStream] Detected cameras:', videoDevices.length, videoDevices);
                const hasMultiple = videoDevices.length > 1;
                setHasMultipleCameras(hasMultiple);

                // Notify parent component
                if (onCameraDetectionChange) {
                    onCameraDetectionChange(hasMultiple);
                }
            } catch (error) {
                console.error('[CameraStream] Error detecting cameras:', error);
                setHasMultipleCameras(false);
                if (onCameraDetectionChange) {
                    onCameraDetectionChange(false);
                }
            }
        };

        // Initial detection
        detectCameras();

        // Re-detect after delay to catch devices after permission is granted
        const timer1 = setTimeout(detectCameras, 1500);
        const timer2 = setTimeout(detectCameras, 3000);

        // Listen for device changes (permission granted, device plugged in, etc.)
        const handleDeviceChange = () => {
            console.log('[CameraStream] Media devices changed, re-detecting...');
            detectCameras();
        };

        if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
        }

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
                navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
            }
        };
    }, [onCameraDetectionChange]);

    // Notify parent when facing mode changes
    useEffect(() => {
        if (onFacingModeChange) {
            onFacingModeChange(facingMode);
        }
    }, [facingMode, onFacingModeChange]);

    // Suppress WASM and BarcodeDetector runtime errors
    useEffect(() => {
        const handleError = (event) => {
            const errorMessage = event?.message || event?.error?.message || '';
            if (
                errorMessage.includes('wasm') ||
                errorMessage.includes('Aborted') ||
                errorMessage.includes('async and sync fetching') ||
                errorMessage.includes('BarcodeDetector') ||
                errorMessage.includes('Barcode detection service unavailable')
            ) {
                console.log('[CameraStream] Scanner error suppressed:', errorMessage);
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
        };

        const handleUnhandledRejection = (event) => {
            const errorMessage = event?.reason?.message || event?.reason?.toString() || '';
            if (
                errorMessage.includes('wasm') ||
                errorMessage.includes('Aborted') ||
                errorMessage.includes('async and sync fetching') ||
                errorMessage.includes('BarcodeDetector') ||
                errorMessage.includes('Barcode detection service unavailable')
            ) {
                console.log('[CameraStream] Unhandled promise rejection suppressed:', errorMessage);
                event.preventDefault();
                return true;
            }
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    const togglePermissionModal = () => {
        setIsPermissionModalOpen(!isPermissionModalOpen);
    };

    const handleScan = (result) => {
        console.log('[CameraStream] handleScan called:', result);
        if (result && result.length > 0) {
            const detectedCode = result[0];
            console.log('[CameraStream] QR/Barcode detected:', detectedCode.rawValue);
            setDetectedText({ text: detectedCode.rawValue });

            // Re-detect cameras on first scan to ensure we have latest device list
            if (!hasMultipleCameras) {
                detectCamerasNow();
            }
        }
    };

    const detectCamerasNow = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            console.log('[CameraStream] Re-detected cameras:', videoDevices.length);
            const hasMultiple = videoDevices.length > 1;
            setHasMultipleCameras(hasMultiple);

            // Notify parent component
            if (onCameraDetectionChange) {
                onCameraDetectionChange(hasMultiple);
            }
        } catch (error) {
            console.error('[CameraStream] Error re-detecting cameras:', error);
        }
    };

    const handleError = async (error) => {
        // Suppress WASM and BarcodeDetector errors
        const errorMessage = error?.toString() || error?.message || '';
        if (
            errorMessage.includes('wasm') ||
            errorMessage.includes('Aborted') ||
            errorMessage.includes('async and sync fetching') ||
            errorMessage.includes('BarcodeDetector') ||
            errorMessage.includes('Barcode detection service unavailable')
        ) {
            console.log('[CameraStream] Scanner error suppressed:', errorMessage);
            return;
        }

        console.error('Scanner error:', error);
        if (error?.name === 'NotAllowedError') {
            // Check if permission API is available to determine if permanently denied
            let isPermanentlyDenied = false;

            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const result = await navigator.permissions.query({ name: 'camera' });
                    console.log('[CameraStream] Camera permission state:', result.state);
                    // If state is 'denied', it means user previously denied and it's sticky
                    isPermanentlyDenied = result.state === 'denied';
                } catch (permError) {
                    console.log('[CameraStream] Permissions API not fully supported:', permError);
                    // On iOS/Safari, permissions API may not work, assume denied is permanent
                    isPermanentlyDenied = true;
                }
            } else {
                // Permissions API not available (older browsers), assume it might be permanent
                isPermanentlyDenied = true;
            }

            setPermissionDeniedPermanently(isPermanentlyDenied);
            togglePermissionModal();
        } else {
            toast.error('Failed to access camera. Please check your device settings.');
        }
    };

    const requestCameraAccessAgain = () => {
        console.log('[CameraStream] Requesting camera access again');
        // Close modal
        togglePermissionModal();

        if (permissionDeniedPermanently) {
            // On iOS/Android PWA, we can't re-trigger the permission prompt
            // User must go to Settings manually
            console.log('[CameraStream] Permissions permanently denied, user must enable in Settings');
            return;
        }

        // Force Scanner to remount and re-request permissions (works on desktop/first time)
        setScannerKey(prev => prev + 1);
    };

    // Detect platform for showing appropriate instructions
    const getPlatform = () => {
        const ua = navigator.userAgent;
        if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
        if (/android/i.test(ua)) return 'Android';
        return 'Desktop';
    };

    const handleBackPress = () => {
        console.log('[CameraStream] Back button pressed, navigating back');
        backPress();
    };

    useImperativeHandle(ref, () => ({
        stopCamera: () => {
            console.log('[CameraStream] stopCamera called - Scanner handles cleanup automatically');
            // The Scanner component handles cleanup automatically on unmount
            // No manual cleanup needed
        },
        resetCamera: async () => {
            console.log('[CameraStream] Resetting camera');
            try {
                // Reset to back camera
                setFacingMode('environment');
            } catch (error) {
                console.error('Error resetting camera:', error);
            }
        },
        switchCamera: async () => {
            if (!hasMultipleCameras) {
                console.log('[CameraStream] Only one camera detected, switch disabled');
                return;
            }

            console.log('[CameraStream] Switching camera');
            try {
                // Turn off torch when switching
                if (isTorchOn) {
                    setIsTorchOn(false);
                }

                // Toggle between front and back camera
                setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
            } catch (error) {
                console.error('Error switching camera:', error);
            }
        },
        hasMultipleCameras: () => hasMultipleCameras,
        takePhoto: async () => {
            console.log('[CameraStream] Taking photo');
            return new Promise((resolve, reject) => {
                try {
                    // Get the video element from the Scanner
                    const videoElement = document.querySelector('video');

                    if (!videoElement) {
                        toast.error('Camera not ready. Please try again.');
                        reject(new Error('Video stream not available'));
                        return;
                    }

                    // Create a canvas element
                    const canvas = document.createElement('canvas');
                    canvas.width = videoElement.videoWidth;
                    canvas.height = videoElement.videoHeight;

                    // Draw the current video frame
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(videoElement, 0, 0);

                    // Convert to blob
                    canvas.toBlob((blob) => {
                        if (blob) {
                            toast.success('Photo captured successfully');
                            resolve(blob);
                        } else {
                            toast.error('Failed to capture photo');
                            reject(new Error('Failed to create blob'));
                        }
                    }, 'image/jpeg', 0.95); // 95% quality JPEG
                } catch (error) {
                    console.error('Error capturing photo:', error);
                    toast.error('Failed to capture photo. Please try again.');
                    reject(error);
                }
            });
        },
        toggleTorch: async () => {
            console.log('[CameraStream] Toggling torch');
            try {
                // Get the video element
                const videoElement = document.querySelector('video');
                if (!videoElement || !videoElement.srcObject) {
                    console.warn('[CameraStream] Camera not ready for torch');
                    return;
                }

                const stream = videoElement.srcObject;
                const track = stream.getVideoTracks()[0];

                if (!track) {
                    console.warn('[CameraStream] No video track found');
                    return;
                }

                // Check if torch is supported
                const capabilities = track.getCapabilities();
                if (!capabilities.torch) {
                    console.log('[CameraStream] Flashlight not supported on this camera');
                    return;
                }

                const newTorchState = !isTorchOn;
                await track.applyConstraints({
                    advanced: [{ torch: newTorchState }]
                });

                setIsTorchOn(newTorchState);
                console.log(`[CameraStream] Flashlight ${newTorchState ? 'on' : 'off'}`);
            } catch (error) {
                console.error('[CameraStream] Error toggling torch:', error);
            }
        }
    }));

    return (
        <div className="h-full w-full relative">
            <ErrorBoundary>
                <Scanner
                    key={scannerKey}
                    ref={scannerRef}
                    onScan={handleScan}
                    onError={handleError}
                    constraints={{
                        facingMode: facingMode,
                        aspectRatio: { ideal: 1 }
                    }}
                    components={{
                        audio: false,
                        onOff: false,
                        torch: false,
                        zoom: false,
                        finder: false
                    }}
                    formats={[
                        'qr_code',
                        'code_128',
                        'ean_13',
                        'ean_8',
                        'upc_a',
                        'upc_e',
                        'code_39',
                        'code_93',
                        'itf',
                        'codabar'
                    ]}
                    styles={{
                        container: {
                            width: '100%',
                            height: '100%',
                            position: 'relative'
                        },
                        video: {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }
                    }}
                />
            </ErrorBoundary>

            <BringzzOverlay isOpen={isPermissionModalOpen} onClose={togglePermissionModal}>
                {permissionDeniedPermanently ? (
                    // Show instructions for manually enabling camera in Settings
                    <>
                        <div className="flex flex-col text-center items-center space-y-2 px-8 py-4">
                            <BringzzText tag="h2">Camera Access Required</BringzzText>
                            <BringzzText tag="h4" className="font-normal text-center">
                                Please enable camera access in your device settings:
                            </BringzzText>
                            {getPlatform() === 'iOS' && (
                                <BringzzText tag="p" className="text-sm text-left mt-2">
                                    1. Open <strong>Settings</strong> on your iPhone<br/>
                                    2. Scroll down and tap <strong>Safari</strong> or <strong>Bringzz</strong><br/>
                                    3. Tap <strong>Camera</strong><br/>
                                    4. Select <strong>Allow</strong><br/>
                                    5. Return to this app and refresh
                                </BringzzText>
                            )}
                            {getPlatform() === 'Android' && (
                                <BringzzText tag="p" className="text-sm text-left mt-2">
                                    1. Open <strong>Settings</strong> on your phone<br/>
                                    2. Tap <strong>Apps</strong> or <strong>Applications</strong><br/>
                                    3. Find and tap <strong>Chrome</strong> or <strong>Bringzz</strong><br/>
                                    4. Tap <strong>Permissions</strong><br/>
                                    5. Tap <strong>Camera</strong> and select <strong>Allow</strong><br/>
                                    6. Return to this app and refresh
                                </BringzzText>
                            )}
                            {getPlatform() === 'Desktop' && (
                                <BringzzText tag="p" className="text-sm text-left mt-2">
                                    Check your browser&apos;s address bar for camera permission settings,
                                    or go to your browser settings to enable camera access for this site.
                                </BringzzText>
                            )}
                        </div>

                        <div className='divide-y'>
                            <div></div>
                            <BringzzText tag="h4" className="font-bold py-2 text-center text-magic-lilac cursor-pointer" onClick={handleBackPress}>
                                Got it
                            </BringzzText>
                        </div>
                    </>
                ) : (
                    // Show initial permission request
                    <>
                        <div className="flex flex-col text-center items-center space-y-2 px-12 py-4">
                            <BringzzText tag="h2">Allow &ldquo;bringzz&rdquo; to use your camera?</BringzzText>
                            <BringzzText tag="h4" className="font-normal text-center">
                                This must be approved to use the scanner
                            </BringzzText>
                        </div>

                        <div className='divide-y'>
                            <div></div>
                            <BringzzText tag="h4" className="font-bold py-2 text-center text-magic-lilac cursor-pointer" onClick={requestCameraAccessAgain}>
                                Yes, while using the app.
                            </BringzzText>
                            <BringzzText tag="h4" className="font-bold py-2 text-center cursor-pointer text-red-400" onClick={handleBackPress}>
                                No
                            </BringzzText>
                        </div>
                    </>
                )}
            </BringzzOverlay>
        </div>
    );
});

CameraStream.displayName = 'CameraStream';

export default CameraStream;
